import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Search, 
  ShieldAlert, 
  ChevronLeft, 
  BookOpen, 
  User, 
  Settings, 
  HelpCircle,
  Undo2,
  Compass
} from 'lucide-react';

const NotFound = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50/50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      
      {/* Decorative Background Elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-100/40 rounded-full blur-[120px] -z-10 animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[40%] bg-blue-100/40 rounded-full blur-[100px] -z-10"></div>

      <div className="max-w-md mx-auto w-full text-center space-y-10 animate-in fade-in zoom-in duration-700">
        
        {/* 404 Illustration Area */}
        <div className="relative inline-block">
          <div className="w-48 h-48 bg-gradient-to-br from-primary-600 to-primary-800 rounded-[3rem] shadow-2xl shadow-primary-200 flex items-center justify-center transform rotate-6 hover:rotate-0 transition-transform duration-500 group">
             <div className="text-center transform -rotate-6 group-hover:rotate-0 transition-transform duration-500">
                <span className="text-6xl font-black text-white block leading-none">404</span>
                <span className="text-xs font-black text-primary-200 uppercase tracking-[0.3em] mt-2 block">Error</span>
             </div>
          </div>
          {/* Floating Icons */}
          <div className="absolute -top-4 -right-4 bg-white p-3 rounded-2xl shadow-xl animate-bounce">
            <Compass className="text-primary-600" size={24} />
          </div>
          <div className="absolute -bottom-4 -left-4 bg-white p-3 rounded-2xl shadow-xl animate-pulse">
            <HelpCircle className="text-amber-500" size={24} />
          </div>
        </div>

        {/* Message Content */}
        <div className="space-y-3 px-4">
          <h1 className="text-4xl font-black text-gray-900 tracking-tight leading-tight">
            عذراً، لقد تهت في الفضاء!
          </h1>
          <p className="text-gray-500 font-medium text-lg leading-relaxed">
            الصفحة التي تحاول الوصول إليها غير موجودة حالياً، ربما تم تغيير مسارها أو حذفها نهائياً.
          </p>
        </div>

        {/* Action Buttons Grid */}
        <div className="grid grid-cols-1 gap-4 px-4">
          <button
            onClick={() => navigate(-1)}
            className="w-full py-4 bg-white border border-gray-100 rounded-[2rem] font-black text-gray-700 shadow-sm hover:shadow-xl hover:bg-gray-50 transition-all flex items-center justify-center gap-3 transform active:scale-95"
          >
            <Undo2 size={20} className="text-primary-600" />
            العودة للصفحة السابقة
          </button>

          <Link
            to="/"
            className="w-full py-5 bg-primary-600 text-white rounded-[2rem] font-black text-lg shadow-2xl shadow-primary-200 hover:bg-primary-700 transition-all flex items-center justify-center gap-3 transform active:scale-95"
          >
            <Home size={22} fill="white" />
            الذهاب للرئيسية
          </Link>
        </div>

        {/* Smart Navigation Suggestions */}
        <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] p-8 border border-white shadow-xl shadow-gray-200/50 mx-4">
          <div className="flex items-center justify-center gap-2 text-gray-400 font-black text-[10px] uppercase tracking-widest mb-6">
            <Search size={14} /> هل تبحث عن وجهة بديلة؟
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            {[
              { name: 'تصفح الكورسات المتاحة', path: '/courses', icon: BookOpen, color: 'text-blue-500' },
              { name: 'عرض ملفك الشخصي', path: '/profile', icon: User, color: 'text-emerald-500' },
              { name: 'إعدادات الحساب', path: '/settings', icon: Settings, color: 'text-purple-500' }
            ].map((link, index) => (
              <Link
                key={index}
                to={link.path}
                className="flex items-center justify-between p-4 rounded-2xl bg-white border border-gray-50 hover:border-primary-200 hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center ${link.color} group-hover:scale-110 transition-transform`}>
                        <link.icon size={16} />
                    </div>
                    <span className="text-sm font-bold text-gray-700 group-hover:text-primary-600 transition-colors">{link.name}</span>
                </div>
                <ChevronLeft size={16} className="text-gray-300 group-hover:text-primary-600 group-hover:translate-x-[-4px] transition-all" />
              </Link>
            ))}
          </div>
        </div>

        {/* Support Footer Info */}
        <div className="pt-6 space-y-4 px-6">
            <div className="inline-flex items-center gap-2 bg-white px-5 py-2 rounded-full border border-gray-100 shadow-sm">
                <ShieldAlert className="text-amber-500" size={16} />
                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">كود الحالة: ERR_PAGE_NOT_FOUND</span>
            </div>
            <p className="text-xs font-medium text-gray-400 leading-relaxed">
                إذا كنت تعتقد أن هذا الخطأ تقني، يرجى <button type="button" className="text-primary-600 font-black hover:underline bg-none cursor-pointer">مراسلة الدعم الفني</button> فوراً لمساعدتك.
            </p>
        </div>
      </div>
    </div>
  );
};

export default NotFound;